// src/services/BindingReportService.js
import { isProductAllowed, productDisplayName } from "../data/bindingProducts";

/**
 * Reads binding sync-metric reports from a same-origin base URL.
 * In prod, BINDING_REPORTS_URL points at the APIM proxy (/binding-reports)
 * that fronts the orbis-binding-sync-reports GitHub repo. In dev it points
 * at a static fixture under /binding-reports.
 *
 * This is the only place that knows the report wire format. To migrate to the
 * PRO GetProductReleaseInfo API later, replace this class (same public methods).
 */
export class HttpBindingReportService {
  /** @param {string} baseUrl e.g. "/binding-reports" */
  constructor(baseUrl) {
    this._baseUrl = (baseUrl || "").replace(/\/$/, "");
    this._index = null; // cached reports.json
  }

  /** Fetch (or re-fetch) reports.json. Always re-fetches so the dropdown stays fresh. */
  async refreshIndex() {
    const res = await fetch(`${this._baseUrl}/reports.json`, {
      cache: "no-store",
    });
    if (!res.ok) {
      throw new Error(`reports.json ${res.status} ${res.statusText}`);
    }
    this._index = await res.json();
    return this._index;
  }

  /** @returns {Array<{code:string, displayName:string}>} allowed products, sorted by name. */
  listProducts() {
    if (!this._index) return [];
    return Object.keys(this._index)
      .filter(isProductAllowed)
      .map((code) => ({ code, displayName: productDisplayName(code) }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  /**
   * @returns {Array<{name:string, reportPath:string}>}
   * versions for a product, newest first.
   */
  listVersions(scope) {
    if (!this._index || !this._index[scope]) return [];
    const all = [];
    for (const year of Object.keys(this._index[scope])) {
      for (const r of this._index[scope][year]) {
        all.push({
          name: r.name,
          reportPath: r.report_path,
        });
      }
    }
    // names sort lexicographically newest-last; reverse for newest-first.
    return all.sort((a, b) => (a.name < b.name ? 1 : a.name > b.name ? -1 : 0));
  }

  /**
   * Fetch a version's report and flatten every bound revision into a Map.
   * Includes parents (e.g. 6 / 29351), their children, and independentLayers.
   * @returns {Promise<Map<number, number>>} layerId -> revisionId
   */
  async getProductMap(reportPath) {
    const res = await fetch(`${this._baseUrl}/${reportPath}`, {
      cache: "no-store",
    });
    if (!res.ok) {
      throw new Error(`${reportPath} ${res.status} ${res.statusText}`);
    }
    const report = await res.json();
    const map = new Map();
    const add = (node) => {
      const v = node && node.boundVersion && node.boundVersion.version;
      if (
        v &&
        typeof v.layerId === "number" &&
        typeof v.revisionId === "number"
      ) {
        map.set(v.layerId, v.revisionId);
      }
    };
    for (const parent of report.parents || []) {
      add(parent);
      for (const child of parent.children || []) add(child);
    }
    for (const ind of report.independentLayers || []) add(ind);
    return map;
  }
}
